import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { queryClient } from '@/api/queryClient';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { Toast } from '@/components/common/Toast';
import { AuthProvider } from '@/context/AuthContext';
import { NavigationProvider } from '@/context/NavigationContext';
import { ReadingDraftProvider } from '@/context/ReadingDraftContext';
import { ToastProvider } from '@/context/ToastContext';
import { ResponsiveLayout } from '@/layouts/ResponsiveLayout';
import { RouteTracker } from '@/components/navigation/RouteTracker';
import { ScrollToTop } from '@/components/navigation/ScrollToTop';
import { HomePage } from '@/pages/HomePage';
import { LoadingPage } from '@/pages/LoadingPage';
import { LoginPage } from '@/pages/LoginPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { ResultPage } from '@/pages/ResultPage';
import { SpreadPage } from '@/pages/SpreadPage';
import { VerifyEmailPage } from '@/pages/VerifyEmailPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { theme } from '@/app/theme';

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>
            <ScrollToTop />
            <RouteTracker />
            <AuthProvider>
              <ReadingDraftProvider>
                <NavigationProvider>
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route
                      element={
                        <RequireAuth>
                          <ResponsiveLayout />
                        </RequireAuth>
                      }
                    >
                      <Route path="/" element={<HomePage />} />
                      <Route path="/spread" element={<SpreadPage />} />
                      <Route path="/loading" element={<LoadingPage />} />
                      <Route path="/result" element={<ResultPage />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Route>
                  </Routes>
                  <Toast />
                </NavigationProvider>
              </ReadingDraftProvider>
            </AuthProvider>
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
